import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase";
import { collection, query, where, getDocs, onSnapshot, orderBy } from "firebase/firestore";

function Navigation() {
    const { isAdmin, currentUser } = useAuth();
    const location = useLocation();
    const [upcomingEventsCount, setUpcomingEventsCount] = useState(0);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [notificationBadge, setNotificationBadge] = useState({ hasUnread: false, count: 0 });
    // Red-dot state: unread notifications + unread chat messages
    const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
    const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
    const [hasUnreadAdminMessages, setHasUnreadAdminMessages] = useState(false);
    // Admins: how many unread player messages (shown as a number, not a dot)
    const [unreadAdminMessageCount, setUnreadAdminMessageCount] = useState(0);

    useEffect(() => {
        if (currentUser) {
            fetchUpcomingEventsCount();
            // FIXED: Only check user's own notifications
            setupUserNotificationListener();
        }
    }, [currentUser]);

    // Red dot on "My Events": unread notifications OR unread chat messages.
    useEffect(() => {
        if (!currentUser) {
            setHasUnreadNotifications(false);
            setHasUnreadMessages(false);
            setHasUnreadAdminMessages(false);
            setUnreadAdminMessageCount(0);
            return;
        }

        // Unread notifications (the same `notifications` collection MyEvents uses)
        const notifUnsub = onSnapshot(
            query(collection(db, "notifications"), where("userId", "==", currentUser.uid)),
            (snap) => setHasUnreadNotifications(snap.docs.some((d) => d.data().read !== true)),
            () => {}
        );

        // Unread messages addressed to this user (admin → player DMs)
        const msgUnsub = onSnapshot(
            query(collection(db, "conversations"), where("userId", "==", currentUser.uid)),
            (snap) => {
                const total = snap.docs.reduce((sum, d) => sum + (d.data().unreadByUser || 0), 0);
                setHasUnreadMessages(total > 0);
            },
            () => {}
        );

        // For admins, also reflect unread messages from players (unreadByAdmin)
        let adminMsgUnsub = null;
        if (isAdmin) {
            // Only conversations that actually have unread messages — this used
            // to listen to the ENTIRE conversations collection (every chat ever)
            // just to add up one number.
            adminMsgUnsub = onSnapshot(
                query(collection(db, "conversations"), where("unreadByAdmin", ">", 0)),
                (snap) => {
                    // Same rules as the admin inbox (MyEvents.js) and the
                    // message toast: skip archived / empty chats, and admin-to-
                    // admin DMs I sent myself.
                    const total = snap.docs.reduce((sum, d) => {
                        const c = d.data();
                        if (c.archivedByAdmin || !c.lastMessage) return sum;
                        if (c.isAdminToAdmin === true && c.adminId === currentUser.uid) return sum;
                        return sum + (c.unreadByAdmin || 0);
                    }, 0);
                    setUnreadAdminMessageCount(total);
                    setHasUnreadAdminMessages(total > 0);
                },
                () => {}
            );
        }

        return () => {
            notifUnsub();
            msgUnsub();
            if (adminMsgUnsub) adminMsgUnsub();
        };
    }, [currentUser, isAdmin]);

    // FIXED: Setup listener for user's own notifications only
    
const setupUserNotificationListener = () => {
    if (!currentUser) return;

    // Query user's own registrations for pending promotions
    const registrationsQuery = query(
        collection(db, "registrations"), 
        where("userId", "==", currentUser.uid), 
        where("paymentStatus", "==", "promotion_pending")
    );

    return onSnapshot(registrationsQuery, (snapshot) => {
        const now = new Date();
        let pendingCount = 0;

        snapshot.forEach((doc) => {
            const registration = doc.data();

            // FIXED: Skip cancelled registrations, especially "not interested" ones
            if (registration.status === 'cancelled' || 
                registration.cancellationReason === 'user_not_interested') {
                return; // Skip this registration
            }

            // Check if promotion hasn't expired
            if (registration.promotionExpiresAt) {
                const expiresAt = new Date(registration.promotionExpiresAt);
                if (expiresAt > now && !registration.paymentDeadlineExpired) {
                    pendingCount++;
                }
            }
        });

        setNotificationBadge({
            hasUnread: pendingCount > 0,
            count: pendingCount,
        });
    });
}; 

    // Listen for location changes to refresh count when navigating between pages
    useEffect(() => {
        if (currentUser && location.pathname === "/my-events") {
            const timer = setTimeout(() => {
                fetchUpcomingEventsCount();
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [location.pathname, currentUser]);

    // Listen for custom events to refresh count
    useEffect(() => {
        const handleRefreshCount = () => {
            if (currentUser) {
                fetchUpcomingEventsCount();
            }
        };

        window.addEventListener("registrationChanged", handleRefreshCount);
        window.addEventListener("eventCancelled", handleRefreshCount);
        window.addEventListener("eventCheckedIn", handleRefreshCount);

        return () => {
            window.removeEventListener("registrationChanged", handleRefreshCount);
            window.removeEventListener("eventCancelled", handleRefreshCount);
            window.removeEventListener("eventCheckedIn", handleRefreshCount);
        };
    }, [currentUser]);

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            setShowScrollButton(scrollTop > 300);
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Don't show navigation on login/register pages
    if (location.pathname === "/login" || location.pathname === "/register") {
        return null;
    }

    const fetchUpcomingEventsCount = async () => {
        try {
            // Fetch user's registrations
            const registrationsQuery = query(collection(db, "registrations"), where("userId", "==", currentUser.uid), where("status", "!=", "cancelled"));

            const registrationsSnapshot = await getDocs(registrationsQuery);
            const userRegistrations = [];
            const eventIds = [];

            registrationsSnapshot.forEach((doc) => {
                const registration = { id: doc.id, ...doc.data() };
                userRegistrations.push(registration);
                if (eventIds.indexOf(registration.eventId) === -1) {
                    eventIds.push(registration.eventId);
                }
            });

            if (eventIds.length > 0) {
                // Fetch events and filter for upcoming ones
                const batches = [];
                const eventIdsCopy = [...eventIds];
                while (eventIdsCopy.length) {
                    const batch = eventIdsCopy.splice(0, 10);
                    batches.push(batch);
                }

                let upcomingCount = 0;
                const now = new Date();

                for (const batch of batches) {
                    const eventsQuery = query(collection(db, "events"), where("__name__", "in", batch));

                    const eventsSnapshot = await getDocs(eventsQuery);
                    eventsSnapshot.forEach((doc) => {
                        const event = { id: doc.id, ...doc.data() };
                        const eventDate = new Date(event.startDate);
                        if (eventDate > now) {
                            upcomingCount++;
                        }
                    });
                }

                setUpcomingEventsCount(upcomingCount);
            } else {
                setUpcomingEventsCount(0);
            }
        } catch (error) {
            console.error("Error fetching upcoming events count:", error);
            setUpcomingEventsCount(0);
        }
    };

    // FIXED: Only show notification badge for user's own pending promotions
    const getTotalBadgeCount = () => {
        return notificationBadge.hasUnread ? notificationBadge.count : 0;
    };

    // Check if there are urgent notifications for this user
    const hasUrgentNotifications = () => {
        return notificationBadge.hasUnread && notificationBadge.count > 0;
    };

    // Show a red dot on "My Events" when there are unread notifications or messages
    const showMyEventsDot = hasUnreadNotifications || hasUnreadMessages || hasUnreadAdminMessages;

    const navigationItems = [
        {
            path: "/info",
            icon: (active) => (
                <svg className={`w-6 h-6 ${active ? (isAdmin ? "text-orange-600" : "text-orange-600") : "text-gray-400"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            ),
            label: "Club News",
            badge: null,
            urgentBadge: false,
        },
        {
            path: "/",
            icon: (active) => (
                <svg className={`w-6 h-6 ${active ? (isAdmin ? "text-orange-600" : "text-orange-600") : "text-gray-400"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
            ),
            label: "Events",
            badge: null,
            urgentBadge: false,
        },
        {
            path: "/my-events",
            icon: (active) => (
                <svg className={`w-6 h-6 ${active ? (isAdmin ? "text-orange-600" : "text-orange-600") : "text-gray-400"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
            ),
            label: "My Events",
            badge: getTotalBadgeCount() > 0 ? getTotalBadgeCount() : null,
            urgentBadge: hasUrgentNotifications(),
            redDot: showMyEventsDot,
            messageCount: isAdmin ? unreadAdminMessageCount : 0,
        }
        // {
        //     path: "/venues",
        //     icon: (active) => (
        //         <svg className={`w-6 h-6 ${active ? (isAdmin ? "text-orange-600" : "text-orange-600") : "text-gray-400"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        //             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        //         </svg>
        //     ),
        //     label: "Venues",
        //     badge: null,
        //     urgentBadge: false,
        // },
    ];

    // Add admin dashboard for admins (before profile)
    if (isAdmin) {
        navigationItems.push({
            path: "/admin",
            icon: (active) => (
                <svg className={`w-6 h-6 ${active ? "text-orange-600" : "text-gray-400"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                </svg>
            ),
            label: "Manage",
            badge: null,
            urgentBadge: false,
        });
    }

    // Add profile at the end
    navigationItems.push({
        path: "/profile",
        icon: (active) => (
            <svg className={`w-6 h-6 ${active ? (isAdmin ? "text-orange-600" : "text-orange-600") : "text-gray-400"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
        ),
        label: "Profile",
        badge: null,
        urgentBadge: false,
    });

    return (
        <>
            {/* Bottom Navigation */}
            <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200">
                <div className="flex justify-around items-center py-2 px-2 max-w-lg mx-auto">
                    {navigationItems.map((item, index) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            end={item.path === "/"}
                            className={({ isActive }) =>
                                `flex flex-col items-center no-underline justify-center py-2 px-2 rounded-lg transition-all duration-200 relative min-w-0 ${isActive ? (isAdmin ? "text-orange-600" : "text-orange-600") : "text-gray-400 hover:text-gray-600"}`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    <div className="relative">
                                        {item.icon(isActive)}

                                        {/* Admin: unread message count (red, top-right) */}
                                        {item.messageCount > 0 && (
                                            <span
                                                className="absolute -top-2 -right-3 bg-red-500 text-white text-[10px] leading-none rounded-full min-w-[1.1rem] h-[1.1rem] px-1 flex items-center justify-center font-bold border-2 border-white shadow-sm"
                                                aria-label={`${item.messageCount} unread messages`}
                                            >
                                                {item.messageCount > 99 ? "99+" : item.messageCount}
                                            </span>
                                        )}

                                        {/* Badge with enhanced styling for urgent notifications
                                            (moves to the top-left when the admin message count is showing) */}
                                        {item.badge && (
                                            <span
                                                className={`absolute -top-2 ${item.messageCount > 0 ? "-left-2" : "-right-2"} text-white text-xs rounded-full min-w-[1rem] h-4 flex items-center justify-center px-1 font-bold ${
                                                    item.urgentBadge
                                                        ? "bg-red-500 animate-pulse shadow-lg" // Urgent notifications (waitlist promotions)
                                                        : isAdmin
                                                        ? "bg-orange-500"
                                                        : "bg-orange-500" // Regular upcoming events count
                                                }`}
                                            >
                                                {item.badge > 99 ? "99+" : item.badge}
                                            </span>
                                        )}

                                        {/* Additional urgent indicator ring */}
                                        {item.urgentBadge && <span className="absolute -top-3 -right-3 w-6 h-6 border-2 border-red-400 rounded-full animate-ping"></span>}

                                        {/* Red dot for unread notifications / messages (when no count badge is shown) */}
                                        {item.redDot && !item.badge && !(item.messageCount > 0) && (
                                            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white"></span>
                                        )}
                                    </div>

                                    <span className={`text-xs font-medium mt-1 truncate ${isActive ? (isAdmin ? "text-orange-600" : "text-orange-600") : "text-gray-400"}`}>{item.label}</span>

                                    {/* Active indicator */}
                                    {isActive && <div className={`absolute -top-1 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full ${isAdmin ? "bg-orange-600" : "bg-orange-600"}`}></div>}
                                </>
                            )}
                        </NavLink>
                    ))}
                </div>
            </nav>

            {/* Floating Action Button (Mobile) */}
            {showScrollButton && (
                <div className="fixed bottom-20 right-4 z-40 sm:hidden">
                    <button
                        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        className={`w-12 h-12 rounded-full border text-white transition-all duration-300 flex items-center justify-center transform shadow-lg ${showScrollButton ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"} ${
                            isAdmin
                                ? "bg-gradient-to-br from-orange-500 to-orange-700 hover:from-orange-600 hover:to-orange-800 border-orange-400"
                                : "bg-gradient-to-br from-orange-500 to-orange-700 hover:from-orange-600 hover:to-orange-800 border-orange-400"
                        }`}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                        </svg>
                    </button>
                </div>
            )}

            {/* Urgent Notification Indicator (Global) */}
            {hasUrgentNotifications() && location.pathname !== "/my-events" && (
                <div className="fixed top-4 right-4 z-50">
                    <div className="bg-red-500 text-white px-3 py-2 rounded-lg shadow-lg animate-bounce">
                        <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            <span className="text-sm font-medium">Payment Required!</span>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default Navigation;